import React, { useEffect, useState } from "react";

type NumberDraftInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> & {
  value: number;
  /** Called with the typed number; unparsable text is not sent. */
  onValueChange: (value: number) => void;
  format?: (value: number) => string;
  /** Sent when the box is cleared; without it clearing keeps the old value. */
  emptyValue?: number;
  /** Whole numbers only: numeric keypad, and no decimal separator, sign or exponent. */
  wholeNumbers?: boolean;
};

const WHOLE_NUMBER_BLOCKED_KEYS = new Set([".", ",", "e", "E", "-", "+"]);

/**
 * Number box that keeps what the user typed instead of re-rendering the
 * formatted number on every keystroke, which jumps the cursor to the end.
 * It shows `format(value)` again on blur, or when `value` changes from
 * outside (slider, quick buttons, clamping).
 */
export const NumberDraftInput: React.FC<NumberDraftInputProps> = ({
  value,
  onValueChange,
  format = String,
  emptyValue,
  wholeNumbers,
  onBlur,
  onKeyDown,
  ...rest
}) => {
  const [draft, setDraft] = useState(() => format(value));

  useEffect(() => {
    setDraft((current) =>
      current.trim() !== "" && Number(current) === value
        ? current
        : format(value),
    );
    // `format` is usually an inline function; only `value` should resync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...rest}
      type="number"
      inputMode={wholeNumbers ? "numeric" : rest.inputMode}
      value={draft}
      onKeyDown={(event) => {
        if (wholeNumbers && WHOLE_NUMBER_BLOCKED_KEYS.has(event.key)) {
          event.preventDefault();
        }
        onKeyDown?.(event);
      }}
      onChange={(event) => {
        const text = event.target.value;
        setDraft(text);
        const parsed = Number(text);
        if (text.trim() === "") {
          if (emptyValue !== undefined) onValueChange(emptyValue);
        } else if (Number.isFinite(parsed)) {
          onValueChange(parsed);
        }
      }}
      onBlur={(event) => {
        setDraft(format(value));
        onBlur?.(event);
      }}
    />
  );
};
